/**
 * Implementacja szyfrowania end-to-end (E2EE) dla aplikacji 2domy
 * Wykorzystuje Web Crypto API z AES-GCM i PBKDF2 zgodnie z TechStack.md
 */

// Interfejs dla zaszyfrowanych danych
export interface EncryptedData {
  iv: string;           // Initialization Vector (wektor inicjalizujący)
  encrypted: string;    // Zaszyfrowana treść
  salt: string;         // Sól do derywacji klucza
}

// Generowanie losowej soli
export const generateSalt = (): Uint8Array => {
  return window.crypto.getRandomValues(new Uint8Array(16));
};

// Generowanie losowego wektora inicjalizującego (IV)
export const generateIV = (): Uint8Array => {
  return window.crypto.getRandomValues(new Uint8Array(12));
};

// Konwersja ArrayBuffer do Base64
export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Konwersja Base64 do ArrayBuffer
export const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

// Derywacja klucza z hasła użytkownika
export const deriveKey = async (
  password: string, 
  salt: Uint8Array
): Promise<CryptoKey> => {
  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    {
      name: 'AES-GCM',
      length: 256
    },
    false,
    ['encrypt', 'decrypt']
  );
};

// Szyfrowanie danych
export const encryptData = async (
  data: string,
  password: string
): Promise<EncryptedData> => {
  const salt = generateSalt();
  const iv = generateIV();
  const key = await deriveKey(password, salt);
  
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv
    },
    key,
    dataBuffer
  );
  
  return {
    iv: arrayBufferToBase64(iv),
    encrypted: arrayBufferToBase64(encryptedBuffer),
    salt: arrayBufferToBase64(salt)
  };
};

// Deszyfrowanie danych
export const decryptData = async (
  encryptedData: EncryptedData,
  password: string
): Promise<string> => {
  const salt = base64ToArrayBuffer(encryptedData.salt);
  const iv = base64ToArrayBuffer(encryptedData.iv);
  const encryptedBuffer = base64ToArrayBuffer(encryptedData.encrypted);
  
  const key = await deriveKey(password, new Uint8Array(salt));
  
  const decryptedBuffer = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(iv)
    },
    key,
    encryptedBuffer
  );
  
  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}; 